<script setup lang="ts">
import { useVModel } from '@vueuse/core'

import type { Partner, PartnerParameter } from '@/types/types'

import type { Store } from '@/views/contests/Components/types/global'

const props = defineProps<{
  modelValue: PartnerParameter | any
  partner: Partner
}>()

const notification = useNotification()

const partnerModel = useVModel(props, 'modelValue')

const { openInNewTab } = useDOM()
const { isUrlValid } = useRegex()
const { t } = useI18n()
const authType = computed(() => props.partner.partnerAuthenticationType?.slug || '')
const { stores } = useLocalStorage()

const connect = () => {
  let url = props.partner.authenticationUrl
  if (props.partner.hasCustomApiUrl && !partnerModel.value.apiUrl) {
    notification.create('error', {
      text: t('pages.marketplace.tabletsPage.form.authentication.customUrlError')
    })
    return
  }
  if (partnerModel.value.apiUrl && !isUrlValid(partnerModel.value.apiUrl)) {
    notification.create('error', {
      text: t('pages.marketplace.tabletsPage.form.authentication.customUrlErrorRegex')
    })
    return
  }
  if (props.partner.hasCustomApiUrl) {
    url = url.replace('{apiUrl}', partnerModel.value.apiUrl)
  }
  openInNewTab(url)
}

const updateSelectedStore = (storeId: number) => {
  partnerModel.value.storeId = storeId
}
</script>

<template>
  <div class="mb-6 px-1">
    <AppFormInput
      v-if="partner.hasCustomApiUrl"
      id="apiUrl"
      v-model="partnerModel.apiUrl"
      type="url"
      name="apiUrl"
      :label="
        partner.partnerText?.api_url_field_label ?? t('pages.marketplace.tabletsPage.form.authentication.websiteLink')
      "
    />
  </div>
  <template v-if="authType === 'basic-auth' || authType === 'password'">
    <div class="mb-6 px-1">
      <AppFormInput
        id="apiKey"
        v-model="partnerModel.apiUsername"
        type="text"
        name="apiKey"
        :label="
          partner.partnerText?.api_username_field_label ??
          (authType === 'basic-auth'
            ? t('pages.marketplace.marketplacePage.form.authentication.apiKey')
            : t('pages.marketplace.tabletsPage.form.authentication.username'))
        "
      />
    </div>
    <div class="mb-6 px-1">
      <AppFormInput
        id="apiKey"
        v-model="partnerModel.apiToken"
        type="text"
        name="apiKey"
        :label="
          partner.partnerText?.api_token_field_label ??
          (authType === 'basic-auth'
            ? t('pages.marketplace.marketplacePage.form.authentication.apiSecret')
            : t('pages.marketplace.tabletsPage.form.authentication.password'))
        "
      />
    </div>
  </template>
  <template v-if="authType === 'token'">
    <div class="mb-6 px-1">
      <AppFormInput
        id="apiKey"
        v-model="partnerModel.apiToken"
        type="text"
        name="apiKey"
        :label="
          partner.partnerText?.api_token_field_label ??
          t('pages.marketplace.tabletsPage.form.authentication.yourApiKey')
        "
      />
    </div>
  </template>
  <template v-if="partner.isMerchantAssociationRequired">
    <div class="mb-6 px-1">
      <AppFormInput
        id="apiKey"
        v-model="partnerModel.merchantIdFromPartner"
        type="text"
        name="apiKey"
        :label="t('pages.marketplace.marketplacePage.form.authentication.merchantIdFromPartner')"
      />
    </div>
  </template>
  <template v-if="partner.isMonoStoreAssociation">
    <div class="px-1">
      <ComboboxSelect
        value-key="id"
        label-key="text"
        :placeholder="t('pages.marketplace.marketplacePage.form.authentication.store')"
        :options="
          stores.map((store: Store) => {
            return { id: store.id, text: store.name }
          })
        "
        :model-value="modelValue.storeId"
        :translate-values="false"
        @update:model-value="updateSelectedStore"
      />
    </div>
  </template>
  <template
    v-if="
      (authType === 'time-limited-token' || !authType) &&
      !partner.isMerchantAssociationRequired &&
      !partner.isMonoStoreAssociation
    "
  >
    <p class="text-left text-sm text-foreground">
      {{ t('pages.marketplace.marketplacePage.form.authentication.timeLimitedToken') }}
    </p>
  </template>
  <template v-if="authType === 'oauth'">
    <div class="mt-4 flex w-full flex-col items-center gap-y-4">
      <p class="text-left text-sm text-foreground">
        {{ t('pages.marketplace.marketplacePage.form.authentication.verifyOauth') }}
      </p>
      <Button variant="default-primary" @click="connect">
        {{ t('pages.marketplace.marketplacePage.form.authentication.accessTo', { partner: partner.name }) }}
        <BaseIcon name="HeroArrowTopRightOnSquareIconOutline" class="ml-1 h-4 w-4" />
      </Button>
    </div>
  </template>
</template>
